import axiosInstance, { ApiResponse } from './axiosInstance';

// Types
export interface AdminProfile {
  id: string;
  email: string;
  name: string;
  role: string;
  permissions: string[] | Record<string, boolean>;
  avatar?: string;
  phone?: string;
  isActive?: boolean;
  lastLoginAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Alias for backward compatibility
export type Admin = AdminProfile;

export interface LoginRequest {
  email: string;
  password: string;
  remember?: boolean;
}

// Backend user response (may have different structure than Admin)
export interface LoginUserResponse {
  id: number | string;
  email: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  type?: string;
  role?: string;
  isVerified?: boolean;
  twoFactorEnabled?: boolean;
  permissions?: string[];
  avatar?: string;
  phone?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface LoginResponse {
  user: LoginUserResponse;
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresIn?: number;
    tokenType?: string;
  };
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export interface UpdateProfileRequest {
  name?: string;
  phone?: string;
  avatar?: string;
}

// Auth API
const authApi = {
  /**
   * Admin login
   */
  login: async (data: LoginRequest): Promise<ApiResponse<LoginResponse>> => {
    const response = await axiosInstance.post('/admin/auth/login', data);
    return response.data;
  },

  /**
   * Admin logout
   */
  logout: async (): Promise<ApiResponse<null>> => {
    const response = await axiosInstance.post('/admin/auth/logout');
    return response.data;
  },

  /**
   * Refresh access token
   */
  refreshToken: async (data: RefreshTokenRequest): Promise<ApiResponse<LoginResponse>> => {
    const response = await axiosInstance.post('/admin/auth/refresh-token', data);
    return response.data;
  },

  /**
   * Get current admin profile
   */
  getProfile: async (): Promise<ApiResponse<Admin>> => {
    console.log('[authApi.getProfile] Calling GET /admin/auth/me...');
    try {
      const response = await axiosInstance.get('/admin/auth/me');
      console.log('[authApi.getProfile] Success:', response.status, response.data);
      return response.data;
    } catch (err) {
      console.error('[authApi.getProfile] FAILED:', err);
      throw err;
    }
  },

  /**
   * Update admin profile
   */
  updateProfile: async (data: UpdateProfileRequest): Promise<ApiResponse<Admin>> => {
    const response = await axiosInstance.put('/admin/auth/profile', data);
    return response.data;
  },

  /**
   * Change password
   */
  changePassword: async (data: ChangePasswordRequest): Promise<ApiResponse<null>> => {
    const response = await axiosInstance.post('/admin/auth/change-password', data);
    return response.data;
  },

  /**
   * Forgot password - request reset link
   */
  forgotPassword: async (data: ForgotPasswordRequest): Promise<ApiResponse<null>> => {
    const response = await axiosInstance.post('/admin/auth/forgot-password', data);
    return response.data;
  },

  /**
   * Reset password with token
   */
  resetPassword: async (data: ResetPasswordRequest): Promise<ApiResponse<null>> => {
    const response = await axiosInstance.post('/admin/auth/reset-password', data);
    return response.data;
  },

  /**
   * Verify reset password token
   */
  verifyResetToken: async (token: string): Promise<ApiResponse<{ valid: boolean }>> => {
    const response = await axiosInstance.get(`/admin/auth/verify-reset-token/${token}`);
    return response.data;
  },
};

export default authApi;
